import { useMemo } from 'react'

import cn from 'classnames'
import { useNavigate } from 'react-router-dom'

import LoadingSpinner from '@src/components/icons/LoadingSpinner'
import { ResultCode, StepNames } from '@src/enums'
import { analyticsService } from '@src/services/analytics'
import { useApplicationStore } from '@src/zustand_stores'
import { useHistoryStore } from '@src/zustand_stores/history.store'
import { useOrderUpdatesBackground } from '@src/zustand_stores/orderUpdates.store'

interface PendingTransactionsButtonProps
    extends React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
    className?: string
}

const PendingTransactionsButton = ({ className, ...props }: PendingTransactionsButtonProps) => {
    // Get pending transactions
    const { pendingOrderIds } = useOrderUpdatesBackground()
    const getOrder = useHistoryStore(state => state.getOrder)
    const openWalletDrawer = useApplicationStore(state => state.openWalletDrawer)
    const navigate = useNavigate()

    // Check if we're in embed mode
    const isEmbed = window.location.pathname.startsWith('/embed')

    // Get token information for each pending transaction
    const pendingTransactions = useMemo(() => {
        return pendingOrderIds
            .map(({ exchangeId, orderId }) => {
                const orderData = getOrder(exchangeId)

                // Skip transactions that have completed or timed out
                if (
                    orderData.currentStep === StepNames.MATCH_TIMED_OUT ||
                    orderData.result?.status === ResultCode.success
                ) {
                    return null
                }

                return {
                    exchangeId,
                    orderId,
                }
            })
            .filter(tx => tx !== null) // Filter out null entries (completed transactions)
    }, [pendingOrderIds, getOrder])

    // If no pending transactions, don't render anything
    if (pendingTransactions.length === 0) {
        return null
    }

    const handleClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        // Track analytics event for pending transactions button click
        analyticsService.trackEvent('pending_tx_button_clicked', {
            timestamp: new Date().toISOString(),
            pending_transaction_count: pendingTransactions.length,
        })

        // Open the wallet portfolio drawer
        openWalletDrawer(true)

        // In embed mode, navigate to /embed/orders
        if (isEmbed) {
            // Preserve URL parameters
            const currentSearch = window.location.search
            navigate(`/embed/orders${currentSearch}`)
        }

        // Call the original onClick if provided
        if (props.onClick) {
            props.onClick(e)
        }
    }

    return (
        <div
            className={cn(
                'flex h-[35px] cursor-pointer items-center gap-2 rounded-xl border border-primary/30 bg-[#1a1b23] px-2 shadow-sm transition-colors hover:bg-slate-800/70 md:h-[50px]',
                className
            )}
            onClick={handleClick}
            {...props}
            aria-label={`${pendingTransactions.length} pending ${pendingTransactions.length === 1 ? 'transaction' : 'transactions'}`}
        >
            <LoadingSpinner className='h-3 w-3 animate-spin text-primary md:h-5 md:w-5' />
            <span className='text-[13px] font-medium text-white md:text-[15px]'>
                <span className='mr-1'>{pendingTransactions.length}</span>
                <span className='max-md:hidden md:inline'>
                    pending {pendingTransactions.length === 1 ? 'tx' : 'txs'}
                </span>
            </span>
        </div>
    )
}

export default PendingTransactionsButton
